import { format, isToday } from 'date-fns';
import { CornerDownRight } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * The inbox look shared by Live traffic and Test chat: a conversation list on
 * the left, the transcript on the right, stacked on phones. The customer sits
 * on the left in grey, Claire on the right in teal, a human agent on the right
 * in amber, and what happened around a reply (tools, guards, verdicts) as
 * small tinted rows under it.
 */

export const CHANNEL_LABELS: Record<string, string> = {
  facebook_messenger: 'Messenger',
  instagram_dm: 'Instagram',
  whatsapp: 'WhatsApp',
  sms: 'SMS',
};

/**
 * The channel a live conversation is on. An SMS conversation that LoopMessage
 * carried over iMessage reads as iMessage, as it does in the clinic inbox.
 */
export function channelLabel(platform: string, transport?: string | null) {
  if (platform === 'sms' && transport === 'imessage') return 'iMessage';
  return CHANNEL_LABELS[platform] ?? platform;
}

export function shortTime(at: string) {
  const date = new Date(at);
  return isToday(date) ? format(date, 'HH:mm') : format(date, 'd MMM HH:mm');
}

export function InboxSplit({
  rail,
  children,
}: {
  rail: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="grid items-start gap-4 md:grid-cols-[300px_minmax(0,1fr)] lg:grid-cols-[320px_minmax(0,1fr)]">
      {rail}
      {children}
    </div>
  );
}

export function InboxRail({
  label,
  header,
  children,
}: {
  label: string;
  header?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <nav
      aria-label={label}
      className="max-h-[40vh] overflow-y-auto rounded-lg border bg-card md:sticky md:top-4 md:max-h-[78vh]"
    >
      {header}
      {children}
    </nav>
  );
}

export function InboxRailItem({
  title,
  time,
  subtitle,
  chips,
  active,
  onSelect,
  testId,
}: {
  title: string;
  time?: string;
  subtitle?: string;
  chips?: React.ReactNode;
  active: boolean;
  onSelect: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? 'true' : undefined}
      data-testid={testId}
      className={cn(
        'block w-full border-b px-3 py-2.5 text-left text-sm transition-colors last:border-b-0 hover:bg-teal-500/5 focus-visible:outline-2 focus-visible:outline-teal-600 focus-visible:-outline-offset-2',
        active && 'bg-teal-500/10 shadow-[inset_3px_0_0] shadow-teal-600'
      )}
    >
      <span className="flex justify-between gap-2">
        <span className="min-w-0 truncate font-semibold">{title}</span>
        {time ? (
          <span className="shrink-0 text-muted-foreground text-xs tabular-nums">
            {time}
          </span>
        ) : null}
      </span>
      {subtitle ? (
        <span className="block truncate text-muted-foreground text-xs">
          {subtitle}
        </span>
      ) : null}
      {chips ? (
        <span className="mt-1.5 flex flex-wrap gap-1">{chips}</span>
      ) : null}
    </button>
  );
}

const CHIP_TONES = {
  plain: 'bg-muted text-muted-foreground',
  tool: 'bg-violet-500/10 text-violet-700 dark:text-violet-300',
  warn: 'bg-rose-500/10 text-rose-700 dark:text-rose-300',
  agent: 'bg-amber-500/15 text-amber-800 dark:text-amber-300',
  ok: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
} as const;

export function InboxChip({
  tone = 'plain',
  children,
}: {
  tone?: keyof typeof CHIP_TONES;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        'whitespace-nowrap rounded-full px-1.5 py-px text-[11px]',
        CHIP_TONES[tone]
      )}
    >
      {children}
    </span>
  );
}

export function InboxPane({
  title,
  meta,
  actions,
  children,
  footer,
  className,
}: {
  title: React.ReactNode;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}) {
  return (
    <article
      className={cn(
        'flex min-w-0 flex-col rounded-lg border bg-card',
        className
      )}
    >
      <header className="flex flex-wrap items-start justify-between gap-2 border-b px-4 py-3 sm:px-5">
        <div className="min-w-0">
          <h2 className="truncate font-semibold text-base">{title}</h2>
          {meta ? (
            <p className="text-muted-foreground text-xs">{meta}</p>
          ) : null}
        </div>
        {actions ? (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </header>
      {children}
      {footer}
    </article>
  );
}

export function InboxThread({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-2.5 px-4 py-4 sm:px-5', className)}>
      {children}
    </div>
  );
}

export type BubbleRole = 'user' | 'bot' | 'agent';

const WHO: Record<BubbleRole, string> = {
  user: 'Customer',
  bot: 'Claire',
  agent: 'Team',
};

export function InboxBubble({
  from: role,
  time,
  who,
  children,
  className,
  testId,
  onContinue,
  continueLabel = 'Continue from here',
}: {
  from: BubbleRole;
  time?: string;
  /** Overrides the label above the bubble. */
  who?: string;
  children: React.ReactNode;
  className?: string;
  testId?: string;
  /** Offers "continue from here": a test conversation up to this message. */
  onContinue?: () => void;
  continueLabel?: string;
}) {
  const mine = role !== 'user';
  return (
    <div
      data-testid={testId}
      className={cn(
        'group/bubble flex max-w-[min(620px,88%)] flex-col gap-1',
        mine ? 'self-end' : 'self-start'
      )}
    >
      <span
        className={cn(
          'text-[11px] uppercase tracking-wide tabular-nums',
          role === 'bot' && 'text-right text-teal-700 dark:text-teal-300',
          role === 'agent' && 'text-right text-amber-700 dark:text-amber-300',
          role === 'user' && 'text-muted-foreground'
        )}
      >
        {who ?? WHO[role]}
        {time ? ` · ${time}` : ''}
        {onContinue ? (
          <button
            type="button"
            onClick={onContinue}
            title={continueLabel}
            className={cn(
              'ml-2 inline-flex items-center gap-1 rounded px-1 align-middle text-muted-foreground normal-case tracking-normal opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover/bubble:opacity-100'
            )}
          >
            <CornerDownRight className="size-3" />
            {continueLabel}
          </button>
        ) : null}
      </span>
      <div
        className={cn(
          'whitespace-pre-wrap break-words rounded-xl px-3 py-2 text-sm leading-relaxed',
          role === 'user' && 'rounded-tl-sm bg-muted',
          role === 'bot' && 'rounded-tr-sm bg-teal-500/10 dark:bg-teal-400/10',
          role === 'agent' && 'rounded-tr-sm bg-amber-500/10',
          className
        )}
      >
        {children}
      </div>
    </div>
  );
}

export function InboxSystem({ children }: { children: React.ReactNode }) {
  return (
    <p className="self-center px-2 text-center text-muted-foreground text-xs">
      {children}
    </p>
  );
}

const EVENT_TONES = {
  tool: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 font-mono text-xs',
  flag: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 text-xs',
  warn: 'bg-amber-500/10 text-amber-800 dark:text-amber-300 text-xs',
  ok: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-xs',
  plain: 'bg-muted text-muted-foreground text-xs',
} as const;

/** A row under Claire's reply: a tool call, a guard, a verdict. */
export function InboxEvent({
  tone,
  children,
  className,
}: {
  tone: keyof typeof EVENT_TONES;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'max-w-[min(620px,92%)] self-end break-words rounded-md px-2.5 py-1.5',
        EVENT_TONES[tone],
        className
      )}
    >
      {children}
    </div>
  );
}

/** "12 replies · 3 flagged": bold numbers in a muted line. */
export function InboxStats({
  items,
}: {
  items: { label: string; value: React.ReactNode; tone?: string }[];
}) {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1 text-muted-foreground text-sm tabular-nums">
      {items.map((item) => (
        <span key={item.label}>
          <b className={cn('mr-1 text-base text-foreground', item.tone)}>
            {item.value}
          </b>
          {item.label}
        </span>
      ))}
    </div>
  );
}

const URL_PATTERN = /(https?:\/\/[^\s<>"')\]]+)/g;

/**
 * Text with its links clickable, opening in a new tab: booking links Claire
 * sends go to the clinic's real booking page.
 */
export function Linkify({ text }: { text: string }) {
  const parts = text.split(URL_PATTERN);
  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noreferrer"
            className="break-all font-medium underline underline-offset-2"
          >
            {part}
          </a>
        ) : (
          part
        )
      )}
    </>
  );
}
